import { Box, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormHelperText, InputLabel, MenuItem, OutlinedInput, Select, Stack } from '@mui/material'
import { FunctionComponent, useContext, useEffect, useState, ChangeEvent } from 'react'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment from 'moment'
import { DialogSaveButtons } from '../../../presentation-components/miscellaneous-components/dialog-save-buttons';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import BuildingTypeService from '../../../../shared/services/building-type/building-type.service';
import LocationService from '../../../../shared/services/location/location.service';
import { IEditFileUploadLineLocation, IFileUploadLineLocation } from '../../../../shared/services/file-upload-all-lines/file-upload-all-lines.model';
import CustomFieldService from '../../../../shared/services/custom-field/custom-field.service';
import { ICustomField } from '../../../../shared/services/custom-field/custom-field.model';
import { IBuildingType, ICountry } from '../../../../shared/services/location/location.model';
import { formatTransaction } from '../../../../shared/utils/format-post-utils';
import { BuildingPeriod } from '../../../../shared/interfaces/billing-period';
import SubsidiaryService from '../../../../shared/services/subsidiary/subsidiary.service';
import { ISubsidiary } from '../../../../shared/services/subsidiary/subsidiary.model';
import { getSelectableSubsidiaries } from '../../../../shared/utils/subsidiary-utils';
import { UserDetailsContext } from '../../../../context/user-details/user-details';

const buildingTypeService = new BuildingTypeService()
const locationService = new LocationService()
const customFieldService = new CustomFieldService()

export const EditUploadLineLocationDialog: FunctionComponent<{
  editUploadLine: IFileUploadLineLocation,
  open: boolean,
  closeHandler: () => void,
  submitHandler: (lineId: string, editUploadLine: IEditFileUploadLineLocation) => void,
  isSubmitting: boolean,
  errorCode?: number,
  errorMessage?: string,
}> = ({
  open,
  closeHandler,
  submitHandler,
  editUploadLine,
  isSubmitting,
  errorCode,
  errorMessage
}) => {
    //Services
    const subsidiaryService = SubsidiaryService.getInstance()
    //Context
    const screenLabelsContext = useContext(ScreenLabelsContext)
    const userDetailsContext = useContext(UserDetailsContext)
    const organisationDetails = userDetailsContext.userDetails?.organisation
    //State
    const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[]>([])
    const [customFields, setCustomFields] = useState<ICustomField[]>()
    const [buildingTypes, setBuildingTypes] = useState<IBuildingType[]>()
    const [countries, setCountries] = useState<ICountry[]>()
    const [openStartDate, setOpenStartDate] = useState(false)
    //Variables
    const ownershipOptions: string[] = ['Leased', 'Owned (Using)', 'Owned (Leased Out)']
    const billingPeriodOptions = Object.values(BuildingPeriod)

    //Formik
    const locationSchema = Yup.object().shape({
      friendlyName: Yup.string().required(`${screenLabelsContext.getLabel('location', { plural: false, capitalize: 'first' })} name is required`),
      buildingTypeId: Yup.string().required('Building type is required'),
      ownership: Yup.string().required('Ownership status is required'),
      locationReference: Yup.string().nullable(),
      hasOperationalControl: Yup.boolean().required('Control status is required'),
      countryId: Yup.string().required('Country is required'),
      addressLineOne: Yup.string().nullable(),
      addressLineTwo: Yup.string().nullable(),
      city: Yup.string().nullable(),
      postcode: Yup.string().nullable(),
      numberOfEmployees: Yup.number().integer().min(0, 'Number of employees cannot be negative').nullable(),
      startDate: Yup.string().required(`You must provide a start date for the ${screenLabelsContext.getLabel('location', { plural: false })}`),
      hasGas: Yup.boolean(),
      hasSteam: Yup.boolean(),
      hasAirCon: Yup.boolean(),
      hasElectricity: Yup.boolean(),
      subsidiaryId: Yup.string().nullable(),
      gasBillingPeriod: Yup.string().when('hasGas', {
        is: true,
        then: schema => schema.required('Frequency of data entry is required'),
        otherwise: schema => schema.notRequired()
      }),
      heatAndSteamBillingPeriod: Yup.string().when('hasSteam', {
        is: true,
        then: schema => schema.required('Frequency of data entry is required'),
        otherwise: schema => schema.notRequired()
      }),
      airConBillingPeriod: Yup.string().when('hasAirCon', {
        is: true,
        then: schema => schema.required('Frequency of data entry is required'),
        otherwise: schema => schema.notRequired()
      }),
      electricityBillingPeriod: Yup.string().when('hasElectricity', {
        is: true,
        then: schema => schema.required('Frequency of data entry is required'),
        otherwise: schema => schema.notRequired()
      }),
      // isPrimaryLocation: Yup.boolean(),
      ...(customFields && customFields.length > 0 ? customFields.reduce((acc, field) => {
        if (field.isMandatory) {
          acc[field.id] = Yup.string().required(`${field.fieldName} is mandatory`);
        } else {
          acc[field.id] = Yup.string().nullable();
        }
        return acc;
      }, {} as any) : {}),
    })

    const formik = useFormik<IEditFileUploadLineLocation & Record<string, string | number | boolean | null | undefined>>({
      initialValues: {
        friendlyName: editUploadLine.friendlyName,
        locationReference: editUploadLine.locationReference,
        addressLineOne: editUploadLine.addressLineOne,
        addressLineTwo: editUploadLine.addressLineTwo,
        city: editUploadLine.city,
        postcode: editUploadLine.postcode,
        buildingTypeId: editUploadLine.buildingTypeId,
        countryId: editUploadLine.countryId,
        startDate: editUploadLine.startDate,
        ownership: editUploadLine.ownership,
        hasOperationalControl: editUploadLine.hasOperationalControl,
        numberOfEmployees: editUploadLine.numberOfEmployees,
        hasGas: editUploadLine.hasGas,
        hasSteam: editUploadLine.hasSteam,
        hasAirCon: editUploadLine.hasAirCon,
        hasElectricity: editUploadLine.hasElectricity,
        gasBillingPeriod: editUploadLine.gasBillingPeriod,
        heatAndSteamBillingPeriod: editUploadLine.heatAndSteamBillingPeriod,
        airConBillingPeriod: editUploadLine.airConBillingPeriod,
        electricityBillingPeriod: editUploadLine.electricityBillingPeriod,
        subsidiaryId: editUploadLine.subsidiaryId,
        // isPrimaryLocation: editUploadLine.isPrimaryLocation,
      },
      validationSchema: locationSchema,
      onSubmit: (values) => {
        const response = formatTransaction(values, '', true, customFields)
        submitHandler(editUploadLine.id, response)
      }
    })

    const { resetForm } = formik

    useEffect(() => {
      customFieldService.getAllActive(`&entryType=location`)
        .then(response => {
          setCustomFields(response)
        })
        .catch(err => console.log(err))

      buildingTypeService.findAll()
        .then(response => {
          setBuildingTypes(response)
        })
        .catch(err => {
          console.log(err)
        })

      locationService.getAllCountries()
        .then(response => {
          setCountries(response)
        })
        .catch(err => {
          console.log(err)
        })

      subsidiaryService.getAll()
        .then(response => {
          setSubsidiaries(response)
        })
        .catch(err => {
          console.log(err)
        })
    }, [editUploadLine])


    useEffect(() => {
      const customFieldValuesObj: Record<string, string> = {}
      if (customFields && customFields.length) {
        customFields.forEach(field => {
          const matchingValue = (editUploadLine as any)?.customFieldValues
            ? (editUploadLine as any).customFieldValues.find((v: any) => v.customFieldId === field.id)?.value ?? ''
            : ''
          customFieldValuesObj[field.id] = matchingValue
        })
      }
      resetForm({
        values: {
          friendlyName: editUploadLine.friendlyName,
          locationReference: editUploadLine.locationReference,
          addressLineOne: editUploadLine.addressLineOne,
          addressLineTwo: editUploadLine.addressLineTwo,
          city: editUploadLine.city,
          postcode: editUploadLine.postcode,
          buildingTypeId: editUploadLine.buildingTypeId,
          countryId: editUploadLine.countryId,
          startDate: editUploadLine.startDate,
          ownership: editUploadLine.ownership,
          hasOperationalControl: editUploadLine.hasOperationalControl,
          numberOfEmployees: editUploadLine.numberOfEmployees,
          hasGas: editUploadLine.hasGas,
          hasSteam: editUploadLine.hasSteam,
          hasAirCon: editUploadLine.hasAirCon,
          hasElectricity: editUploadLine.hasElectricity,
          gasBillingPeriod: editUploadLine.gasBillingPeriod,
          heatAndSteamBillingPeriod: editUploadLine.heatAndSteamBillingPeriod,
          airConBillingPeriod: editUploadLine.airConBillingPeriod,
          electricityBillingPeriod: editUploadLine.electricityBillingPeriod,
          subsidiaryId: editUploadLine.subsidiaryId,
          // isPrimaryLocation: editUploadLine.isPrimaryLocation,
          ...customFieldValuesObj,
        }
      })
    }, [customFields, editUploadLine, resetForm])

    const handleCheckboxChange = (field: string) => (event: ChangeEvent<HTMLInputElement>) => {
      formik.setFieldValue(field as string, event.target.checked)
      formik.setFieldTouched(field as string, true, false)
    }

    const handleUtilityToggle = (field: string, billingField: string) => (event: ChangeEvent<HTMLInputElement>) => {
      handleCheckboxChange(field)(event)
      if (!event.target.checked) {
        formik.setFieldValue(billingField as string, '')
      }
    }

    const cancelHandler = () => {
      formik.resetForm();
      closeHandler();
    }

    return (
      <Dialog open={open} onClose={closeHandler} maxWidth='xs'>
        <Box sx={{ p: 4 }} component='form'>
          <DialogTitle sx={{ pt: 0, pl: 0, pr: 0 }}>
            Edit Uploaded Data
          </DialogTitle>
          <DialogContent sx={{ pt: 0, pl: 0, pr: 0 }}>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='friendlyName'>{screenLabelsContext.getLabel('location', { plural: false, capitalize: 'first' })} Name</InputLabel>
              <OutlinedInput id='friendlyName' label={`${screenLabelsContext.getLabel('location', { plural: false, capitalize: 'first' })} Name`} name='friendlyName'
                value={formik.values.friendlyName}
                onChange={formik.handleChange}
                error={formik.touched.friendlyName && Boolean(formik.errors.friendlyName)}
                sx={{ color: 'text.primary' }}></OutlinedInput>
              {formik.touched.friendlyName && formik.errors.friendlyName &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.friendlyName}</FormHelperText>
              }
              {(errorCode === 1 || errorCode === 2) && errorMessage &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{errorMessage}</FormHelperText>
              }
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='locationReference'>Location Reference</InputLabel>
              <OutlinedInput id='locationReference' label='Location Reference' name='locationReference'
                value={formik.values.locationReference}
                onChange={formik.handleChange}
                error={formik.touched.locationReference && Boolean(formik.errors.locationReference)}
                sx={{ color: 'text.primary' }}></OutlinedInput>
              {formik.touched.locationReference && formik.errors.locationReference &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.locationReference}</FormHelperText>
              }
            </FormControl>
            {/* <FormControl component='fieldset' sx={{ mt: 2 }}>
              <FormControlLabel
                sx={{ ml: 1 }}
                control={
                  <Checkbox
                    checked={formik.values.isPrimaryLocation}
                    onChange={handleCheckboxChange('isPrimaryLocation')}
                    name='isPrimaryLocation'
                  />
                }
                label='Primary Location'
              />
            </FormControl> */}
            {buildingTypes && <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='buildingTypeId'>Building Type</InputLabel>
              <Select
                id='buildingTypeId' label='Building Type' name='buildingTypeId'
                value={formik.values.buildingTypeId || ''}
                onChange={formik.handleChange}
                error={formik.touched.buildingTypeId && Boolean(formik.errors.buildingTypeId)}
                sx={{ color: 'text.primary' }}>
                {buildingTypes.map(buildingType => (
                  <MenuItem key={buildingType.id} value={buildingType.id}>{buildingType.name}</MenuItem>
                ))}
              </Select>
              {formik.touched.buildingTypeId && formik.errors.buildingTypeId &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.buildingTypeId}</FormHelperText>
              }
            </FormControl>}
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='ownership'>Ownership</InputLabel>
              <Select
                id='ownership'
                name='ownership'
                label='Ownership'
                value={formik.values.ownership || ''}
                onChange={formik.handleChange}
                error={formik.touched.ownership && Boolean(formik.errors.ownership)}
                sx={{ color: 'text.primary' }}>
                {ownershipOptions.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
              {formik.touched.ownership && formik.errors.ownership &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.ownership}</FormHelperText>
              }
            </FormControl>
            <FormControl component='fieldset' sx={{ mt: 2 }}>
              <FormControlLabel
                sx={{ ml: 1 }}
                control={
                  <Checkbox
                    checked={formik.values.hasOperationalControl}
                    onChange={handleCheckboxChange('hasOperationalControl')}
                    name='hasOperationalControl'
                  />
                }
                label='Has Operational Control'
              />
              {formik.touched.hasOperationalControl && formik.errors.hasOperationalControl &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.hasOperationalControl}</FormHelperText>
              }
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='addressLineOne'>Address Line 1</InputLabel>
              <OutlinedInput id='addressLineOne' label='Address Line 1' name='addressLineOne'
                value={formik.values.addressLineOne}
                onChange={formik.handleChange}
                error={formik.touched.addressLineOne && Boolean(formik.errors.addressLineOne)}
                sx={{ color: 'text.primary' }}></OutlinedInput>
              {formik.touched.addressLineOne && formik.errors.addressLineOne &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.addressLineOne}</FormHelperText>
              }
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='addressLineTwo'>Address Line 2</InputLabel>
              <OutlinedInput id='addressLineTwo' label='Address Line 2' name='addressLineTwo'
                value={formik.values.addressLineTwo}
                onChange={formik.handleChange}
                error={formik.touched.addressLineTwo && Boolean(formik.errors.addressLineTwo)}
                sx={{ color: 'text.primary' }}></OutlinedInput>
              {formik.touched.addressLineTwo && formik.errors.addressLineTwo &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.addressLineTwo}</FormHelperText>
              }
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='city'>City</InputLabel>
              <OutlinedInput id='city' label='City' name='city'
                value={formik.values.city}
                onChange={formik.handleChange}
                error={formik.touched.city && Boolean(formik.errors.city)}
                sx={{ color: 'text.primary' }}></OutlinedInput>
              {formik.touched.city && formik.errors.city &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.city}</FormHelperText>
              }
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='postcode'>Postcode</InputLabel>
              <OutlinedInput id='postcode' label='Postcode' name='postcode'
                value={formik.values.postcode}
                onChange={formik.handleChange}
                error={formik.touched.postcode && Boolean(formik.errors.postcode)}
                sx={{ color: 'text.primary' }}></OutlinedInput>
              {formik.touched.postcode && formik.errors.postcode &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.postcode}</FormHelperText>
              }
            </FormControl>
            {countries && <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='countryId'>Country</InputLabel>
              <Select
                id='countryId' label='Country' name='countryId'
                value={formik.values.countryId || ''}
                onChange={formik.handleChange}
                error={formik.touched.countryId && Boolean(formik.errors.countryId)}
                sx={{ color: 'text.primary' }}>
                {countries.map(country => (
                  <MenuItem key={country.id} value={country.id}>{country.name}</MenuItem>
                ))}
              </Select>
              {formik.touched.countryId && formik.errors.countryId &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.countryId}</FormHelperText>
              }
            </FormControl>}
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label='Start Date'
                  value={formik.values.startDate ? moment(formik.values.startDate) : null}
                  onChange={(date) => formik.setFieldValue('startDate', date?.format('YYYY-MM-DD'))}
                  format='DD/MM/YYYY'
                  sx={{ color: 'text.primary' }}
                  open={openStartDate}
                  onOpen={() => setOpenStartDate(true)}
                  onClose={() => setOpenStartDate(false)}
                  slotProps={{
                    textField: {
                      onClick: () => setOpenStartDate(true),
                      error: formik.touched.startDate && Boolean(formik.errors.startDate)
                    },
                  }}
                />
              </LocalizationProvider>
              {formik.touched.startDate && formik.errors.startDate &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.startDate}</FormHelperText>
              }
            </FormControl>
            {subsidiaries && (organisationDetails?.enableSubsidiaries || subsidiaries.length > 1) &&
              <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                <InputLabel htmlFor='subsidiaryId'>{screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })}</InputLabel>
                <Select
                  id='subsidiaryId' label={screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })} name='subsidiaryId'
                  value={formik.values.subsidiaryId}
                  onChange={formik.handleChange}
                  error={formik.touched.subsidiaryId && Boolean(formik.errors.subsidiaryId)}
                  sx={{ color: 'text.primary', }}>
                  {getSelectableSubsidiaries(subsidiaries, formik.values.subsidiaryId).map(subsidiary => (
                    <MenuItem key={subsidiary.id} value={subsidiary.id}>{subsidiary.name}</MenuItem>
                  ))}
                </Select>
                {formik.touched.subsidiaryId && formik.errors.subsidiaryId &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.subsidiaryId}</FormHelperText>
                }
              </FormControl>
            }
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='numberOfEmployees'>Number of Employees</InputLabel>
              <OutlinedInput id='numberOfEmployees' label='Number of Employees' name='numberOfEmployees'
                type='number'
                inputProps={{ min: 0 }}
                value={formik.values.numberOfEmployees ?? ''}
                onChange={formik.handleChange}
                error={formik.touched.numberOfEmployees && Boolean(formik.errors.numberOfEmployees)}
                sx={{ color: 'text.primary' }}></OutlinedInput>
              {formik.touched.numberOfEmployees && formik.errors.numberOfEmployees &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.numberOfEmployees}</FormHelperText>
              }
            </FormControl>
            {formik.values.hasOperationalControl && (
              <>
                <Stack direction='column' spacing={1} sx={{ mt: 2 }}>
                  <FormControl component='fieldset' sx={{ m: 0 }}>
                    <FormControlLabel
                      sx={{ ml: 1 }}
                      control={
                        <Checkbox
                          checked={formik.values.hasElectricity}
                          onChange={handleUtilityToggle('hasElectricity', 'electricityBillingPeriod')}
                          name='hasElectricity'
                        />
                      }
                      label='Has Electricity'
                    />
                  </FormControl>
                  {formik.values.hasElectricity && (
                    <FormControl fullWidth variant='outlined' sx={{ m: 0 }}>
                      <InputLabel htmlFor='electricityBillingPeriod'>Electricity Billing Period</InputLabel>
                      <Select
                        id='electricityBillingPeriod'
                        name='electricityBillingPeriod'
                        label='Electricity Billing Period'
                        value={formik.values.electricityBillingPeriod || ''}
                        onChange={formik.handleChange}
                        error={formik.touched.electricityBillingPeriod && Boolean(formik.errors.electricityBillingPeriod)}
                        sx={{ color: 'text.primary' }}>
                        {billingPeriodOptions.map(period => (
                          <MenuItem key={period} value={period}>{period}</MenuItem>
                        ))}
                      </Select>
                      {formik.touched.electricityBillingPeriod && formik.errors.electricityBillingPeriod &&
                        <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.electricityBillingPeriod}</FormHelperText>
                      }
                    </FormControl>
                  )}
                </Stack>
                <Stack direction='column' spacing={1} sx={{ mt: 2 }}>
                  <FormControl component='fieldset' sx={{ m: 0 }}>
                    <FormControlLabel
                      sx={{ ml: 1 }}
                      control={
                        <Checkbox
                          checked={formik.values.hasGas}
                          onChange={handleUtilityToggle('hasGas', 'gasBillingPeriod')}
                          name='hasGas'
                        />
                      }
                      label='Has Gas'
                    />
                  </FormControl>
                  {formik.values.hasGas && (
                    <FormControl fullWidth variant='outlined' sx={{ m: 0 }}>
                      <InputLabel htmlFor='gasBillingPeriod'>Gas Billing Period</InputLabel>
                      <Select
                        id='gasBillingPeriod'
                        name='gasBillingPeriod'
                        label='Gas Billing Period'
                        value={formik.values.gasBillingPeriod || ''}
                        onChange={formik.handleChange}
                        error={formik.touched.gasBillingPeriod && Boolean(formik.errors.gasBillingPeriod)}
                        sx={{ color: 'text.primary' }}>
                        {billingPeriodOptions.map(period => (
                          <MenuItem key={period} value={period}>{period}</MenuItem>
                        ))}
                      </Select>
                      {formik.touched.gasBillingPeriod && formik.errors.gasBillingPeriod &&
                        <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.gasBillingPeriod}</FormHelperText>
                      }
                    </FormControl>
                  )}
                </Stack>
                <Stack direction='column' spacing={1} sx={{ mt: 2 }}>
                  <FormControl component='fieldset' sx={{ m: 0 }}>
                    <FormControlLabel
                      sx={{ ml: 1 }}
                      control={
                        <Checkbox
                          checked={formik.values.hasAirCon}
                          onChange={handleUtilityToggle('hasAirCon', 'airConBillingPeriod')}
                          name='hasAirCon'
                        />
                      }
                      label='Has Air Conditioning'
                    />
                  </FormControl>
                  {formik.values.hasAirCon && (
                    <FormControl fullWidth variant='outlined' sx={{ m: 0 }}>
                      <InputLabel htmlFor='airConBillingPeriod'>Air Con Billing Period</InputLabel>
                      <Select
                        id='airConBillingPeriod'
                        name='airConBillingPeriod'
                        label='Air Con Billing Period'
                        value={formik.values.airConBillingPeriod || ''}
                        onChange={formik.handleChange}
                        error={formik.touched.airConBillingPeriod && Boolean(formik.errors.airConBillingPeriod)}
                        sx={{ color: 'text.primary' }}>
                        {billingPeriodOptions.map(period => (
                          <MenuItem key={period} value={period}>{period}</MenuItem>
                        ))}
                      </Select>
                      {formik.touched.airConBillingPeriod && formik.errors.airConBillingPeriod &&
                        <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.airConBillingPeriod}</FormHelperText>
                      }
                    </FormControl>
                  )}
                </Stack>
                <Stack direction='column' spacing={1} sx={{ mt: 2 }}>
                  <FormControl component='fieldset' sx={{ m: 0 }}>
                    <FormControlLabel
                      sx={{ ml: 1 }}
                      control={
                        <Checkbox
                          checked={formik.values.hasSteam}
                          onChange={handleUtilityToggle('hasSteam', 'heatAndSteamBillingPeriod')}
                          name='hasSteam'
                        />
                      }
                      label='Has Heat and Steam'
                    />
                  </FormControl>
                  {formik.values.hasSteam && (
                    <FormControl fullWidth variant='outlined' sx={{ m: 0 }}>
                      <InputLabel htmlFor='heatAndSteamBillingPeriod'>Heat and Steam Billing Period</InputLabel>
                      <Select
                        id='heatAndSteamBillingPeriod'
                        name='heatAndSteamBillingPeriod'
                        label='Heat and Steam Billing Period'
                        value={formik.values.heatAndSteamBillingPeriod || ''}
                        onChange={formik.handleChange}
                        error={formik.touched.heatAndSteamBillingPeriod && Boolean(formik.errors.heatAndSteamBillingPeriod)}
                        sx={{ color: 'text.primary' }}>
                        {billingPeriodOptions.map(period => (
                          <MenuItem key={period} value={period}>{period}</MenuItem>
                        ))}
                      </Select>
                      {formik.touched.heatAndSteamBillingPeriod && formik.errors.heatAndSteamBillingPeriod &&
                        <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.heatAndSteamBillingPeriod}</FormHelperText>
                      }
                    </FormControl>
                  )}
                </Stack>
              </>
            )}
            {customFields && customFields.length > 0 && customFields.map((customField, index) => {
              return (
                <Stack
                  direction='row' spacing={2} key={index} sx={{ mt: 2 }}>
                  <FormControl fullWidth variant='outlined'>
                    <InputLabel htmlFor={customField.id}> {customField.fieldName}</InputLabel>
                    <OutlinedInput id={customField.id} label={customField.fieldName} name={customField.id}
                      type={customField.fieldType === 'number' ? 'number' : 'text'}
                      value={formik.values[customField.id]}
                      onChange={formik.handleChange}
                      error={formik.touched[customField.id] && Boolean(formik.errors[customField.id])}
                      sx={{ color: 'text.primary' }}></OutlinedInput>
                  </FormControl>
                  {formik.touched[customField.id] && formik.errors[customField.id] &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors[customField.id] ? <div>{formik.errors[customField.id] as string}</div> : null}
                    </FormHelperText>
                  }
                </Stack>
              )
            })}
          </DialogContent>
          <DialogActions sx={{ pt: 2, pl: 0, pr: 0 }}>
            <DialogSaveButtons
              cancelHandler={cancelHandler}
              submitHandler={formik.handleSubmit}
              isSubmitting={isSubmitting}
            />
          </DialogActions>
        </Box>
      </Dialog>
    )
  }
